/**
 * Shared auth-storage helpers.
 *
 * The session itself (access token + refresh token + minimal user snapshot) is
 * persisted under `oktogo_session` in **`localStorage`** (when "Remember me" is
 * checked) or **`sessionStorage`** (when unchecked). Several other keys persist
 * **non-auth** UI state across reloads:
 *
 *  - `oktogo_active_studio` — last selected studio scope (`StudioContext`)
 *  - `oktogo_chats_filters_v1` — chats list filters (`Chats.tsx`)
 *  - `oktogo_surface_*` — per-surface studio scope (`useSurfaceStudioScope`)
 *
 * Before the FE-03 fix, `clearStoredSession()` only removed `oktogo_session`,
 * so after logout the leftover non-auth keys made `localStorage` look like
 * "something is still remembered" — users mistook that for a Remember-me bug
 * and reported it as **FE-03**.
 *
 * `clearAuthStorage()` therefore clears the session **and** the user-scoped
 * UI-state keys above. The locale preference (`oktogo_ui_locale`) is
 * intentionally **preserved** so the next login lands in the same language.
 */

/** Where the session payload lives (one of the two browser stores at a time). */
export const SESSION_STORAGE_KEY = "oktogo_session";

/**
 * Read the persisted session blob from whichever store currently holds it.
 *
 * Falls back to `null` for malformed JSON; callers should treat `null` as
 * "no session" and trigger their existing not-authenticated branch.
 */
export function readStoredSession<T = unknown>(): T | null {
  const raw =
    localStorage.getItem(SESSION_STORAGE_KEY) ||
    sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Auth-state keys removed by every `clearAuthStorage()` call. Kept here (and
 * referenced by tests) so the cleanup surface stays explicit and reviewable.
 *
 * Do not include `oktogo_ui_locale` — UI language is a user preference that
 * should survive logout, and it has its own persistence rules (see
 * `src/lib/i18n/constants.ts`).
 */
export const NON_AUTH_USER_STATE_KEYS = [
  "oktogo_active_studio",
  "oktogo_chats_filters_v1",
] as const;

/**
 * Clear the persisted session AND the non-auth user-state that should not
 * survive logout. Idempotent and safe to call when nothing is stored.
 *
 * Call this from:
 *   - `AuthContext.logout()`
 *   - definitive auth failure during session restore (invalid/expired tokens)
 *   - any future "force sign out" flow
 */
export function clearAuthStorage(): void {
  // Session blob: present in exactly one of the two stores; remove from both.
  localStorage.removeItem(SESSION_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_KEY);

  for (const key of NON_AUTH_USER_STATE_KEYS) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  // Per-surface studio scope (`useSurfaceStudioScope`) lives in sessionStorage
  // under the prefix below. Clear all matching keys so the next user's session
  // starts from a clean per-surface scope.
  const surfacePrefix = "oktogo_surface_";
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(surfacePrefix)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // sessionStorage access can throw in some sandboxed contexts; clearing
    // surface scope is best-effort, the session itself is already removed.
  }
}
